/**
 * WS5 #10 inc 2z — a tiny native evaluator replicating AngularJS
 * `$parse(expr)({})` for the CONSTRAINED object-literal grammar the
 * `ft-touchpoint-trigger` attribute actually carries (worktree inventory:
 * journeys.component.html only — `{campaignUuid: '…', propertyUuid: '…',
 * name: '…'[, dataType: 'styling']}`, the Angular attribute interpolation
 * having already resolved every `{{…}}` to a literal by click time).
 *
 * Supported, matching the AngularJS expression parser evaluated against an
 * EMPTY scope: a single object literal with unquoted or quoted keys;
 * values = single/double-quoted strings (with backslash escapes), number /
 * true / false / null literals, or an identifier path (`a`, `vm.x.y`) which
 * on the empty scope resolves to UNDEFINED exactly like v1. NOT a general
 * expression evaluator — nested objects/arrays/operators/calls fail, and a
 * failure THROWS (the caller's try/catch mirrors the v1 directive swallowing
 * $parse syntax errors).
 *
 * ⚠ History: a prior JSON.parse drain of this exact call silently killed
 * every touchpoint editor (JSON rejects unquoted keys/single quotes) and the
 * load-state gate was blind to it. This port is proven by the golden spec
 * (ngjs-object-literal.util.spec.ts) + the Advocacy Journeys panel-click
 * interaction suite on p/quebec (every "Open X touchpoint" test runs it).
 */
export function evalNgjsObjectLiteral(src: string): any {
  let i = 0;
  const fail = (): never => {
    throw new Error('ngjs-object-literal: unsupported expression');
  };
  const ws = () => {
    while (i < src.length && /\s/.test(src[i])) i++;
  };
  const ident = (): string => {
    const m = /^[$A-Za-z_][$\w]*/.exec(src.slice(i));
    if (!m) return fail();
    i += m[0].length;
    return m[0];
  };
  const quoted = (q: string): string => {
    i++; // opening quote
    let out = '';
    while (i < src.length && src[i] !== q) {
      if (src[i] === '\\') {
        // The AngularJS lexer ESCAPE table is {n,f,r,t,v,',"} + \u four-hex;
        // only n/t/r are mapped here (plus the identical unknown-escape
        // passthrough — ESCAPE[ch]||ch). \f/\v map differently and invalid
        // \u inverts the failure mode (lexer throws, this passes through) —
        // verified-UNREACHABLE divergences (no consumer value can contain a
        // backslash; the 2z differential probe pinned them).
        const n = src[i + 1];
        out += n === 'n' ? '\n' : n === 't' ? '\t' : n === 'r' ? '\r' : n;
        i += 2;
      } else {
        out += src[i++];
      }
    }
    if (src[i] !== q) fail();
    i++;
    return out;
  };
  const value = (): any => {
    ws();
    const c = src[i];
    if (c === "'" || c === '"') return quoted(c);
    const num = /^-?\d+(?:\.\d+)?/.exec(src.slice(i));
    if (num) {
      i += num[0].length;
      return parseFloat(num[0]);
    }
    const path = /^[$A-Za-z_][$\w]*(?:\.[$A-Za-z_][$\w]*)*/.exec(src.slice(i));
    if (!path) return fail();
    i += path[0].length;
    if (path[0] === 'true') return true;
    if (path[0] === 'false') return false;
    if (path[0] === 'null') return null;
    // Any other identifier path = a lookup against the empty scope → undefined
    // (v1 `$parse(expr)({})`; `undefined` itself lands here too).
    return undefined;
  };

  ws();
  if (src[i] !== '{') fail();
  i++;
  const obj: Record<string, any> = {};
  ws();
  if (src[i] === '}') {
    i++;
  } else {
    for (;;) {
      ws();
      const key = src[i] === "'" || src[i] === '"' ? quoted(src[i]) : ident();
      ws();
      if (src[i] !== ':') fail();
      i++;
      obj[key] = value();
      ws();
      if (src[i] === ',') {
        i++;
        continue;
      }
      if (src[i] === '}') {
        i++;
        break;
      }
      fail();
    }
  }
  ws();
  if (i !== src.length) fail();
  return obj;
}
